import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-primary text-white py-16">
        <div className="container mx-auto px-4">
          <Link 
            to="/" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-4xl font-bold">Términos y Condiciones</h1>
              <p className="text-white/70 mt-1">Última actualización: Enero 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <motion.div 
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="prose prose-slate max-w-none">
            
            {/* Section 1 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                1. Identificación del titular
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-2">
                <p><strong>Responsable:</strong> Stampply</p>
                <p><strong>Denominación social:</strong> XP Projects SaaS</p>
                <p><strong>NIF/CIF:</strong> 46636052X</p>
                <p><strong>Correo electrónico:</strong> <a href="mailto:info@stampply.com" className="text-primary hover:underline">info@stampply.com</a></p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                2. Objeto
              </h2>
              <p className="text-slate-700 mb-4">
                Stampply es una plataforma SaaS para comercios que permite crear y gestionar programas de fidelización mediante sellos digitales y la interacción del cliente final a través de WhatsApp y códigos QR, sin necesidad de instalar una app por parte del cliente final.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                <p className="text-blue-800">
                  Stampply actúa como proveedor tecnológico. El comercio es el responsable de su relación con el cliente final, de sus ofertas, recompensas, condiciones comerciales y del cumplimiento normativo aplicable.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                3. Aceptación
              </h2>
              <p className="text-slate-700">
                El uso de la plataforma por parte del comercio implica la aceptación de estos Términos y Condiciones. Si no está de acuerdo, debe abstenerse de utilizar el servicio.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                4. Definiciones
              </h2>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <span className="font-semibold text-primary min-w-[180px]">Comercio / Cliente (B2B):</span>
                  <span className="text-slate-600">Empresa o autónomo que contrata Stampply.</span>
                </div>
                <div className="flex gap-3">
                  <span className="font-semibold text-primary min-w-[180px]">Cliente final:</span>
                  <span className="text-slate-600">Consumidor que participa en el programa del comercio.</span>
                </div>
                <div className="flex gap-3">
                  <span className="font-semibold text-primary min-w-[180px]">Programa/Campaña:</span>
                  <span className="text-slate-600">Configuración de reglas (sellos, recompensa, vigencia) definida por el comercio.</span>
                </div>
                <div className="flex gap-3">
                  <span className="font-semibold text-primary min-w-[180px]">Sello:</span>
                  <span className="text-slate-600">Unidad de progreso del cliente final dentro de un programa del comercio.</span>
                </div>
                <div className="flex gap-3">
                  <span className="font-semibold text-primary min-w-[180px]">Recompensa:</span>
                  <span className="text-slate-600">Beneficio definido por el comercio al completar sellos.</span>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                5. Registro, cuenta y seguridad
              </h2>
              <p className="text-slate-700">
                El comercio debe registrar una cuenta y mantener sus credenciales seguras. Es responsable de toda actividad realizada desde su cuenta y de los accesos de su personal.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                6. Planes, precios y facturación
              </h2>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">6.1. Planes disponibles</h3>
              <p className="text-slate-600 mb-4">Stampply ofrece los siguientes planes de suscripción:</p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <h4 className="font-bold text-slate-800 mb-2">Free (0€/mes)</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• 1 campaña activa</li>
                    <li>• 1 local</li>
                    <li>• 100 sellos/mes</li>
                    <li>• QR + Cartelería</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <h4 className="font-bold text-slate-800 mb-2">Growth (29€/mes)</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• 5 campañas activas</li>
                    <li>• 1 local</li>
                    <li>• 5.000 sellos/mes</li>
                    <li>• Asistente IA, Exportación CSV</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <h4 className="font-bold text-slate-800 mb-2">Scale (95€/mes)</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• 15 campañas activas</li>
                    <li>• 5 locales</li>
                    <li>• 25.000 sellos/mes</li>
                    <li>• Multi-local, Soporte Chat 24h</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <h4 className="font-bold text-slate-800 mb-2">Elite (395€/mes)</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Campañas ilimitadas</li>
                    <li>• Hasta 50 locales</li>
                    <li>• Sellos ilimitados</li>
                    <li>• Todas las funcionalidades</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">6.2. Facturación</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>La facturación se realiza de forma mensual y anticipada.</li>
                <li>El pago se procesa automáticamente mediante el método de pago registrado.</li>
                <li>Los precios indicados no incluyen IVA, que se aplicará según la normativa vigente.</li>
                <li>Las facturas se enviarán por correo electrónico a la dirección asociada a la cuenta.</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">6.3. Cambios de plan y cancelación</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>El comercio puede cambiar de plan en cualquier momento desde su panel de control.</li>
                <li>Las mejoras de plan se aplican inmediatamente; las reducciones al siguiente ciclo de facturación.</li>
                <li>La cancelación puede realizarse en cualquier momento, manteniendo el acceso hasta el fin del período pagado.</li>
                <li>No se realizan reembolsos por períodos no utilizados, salvo obligación legal.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                7. Funcionamiento del servicio y WhatsApp
              </h2>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.1. Flujo de funcionamiento</h3>
              <div className="bg-slate-50 rounded-xl p-5 mb-4">
                <ol className="list-decimal list-inside space-y-2 text-slate-600">
                  <li>El comercio crea una campaña de fidelización definiendo el número de sellos y la recompensa.</li>
                  <li>El comercio genera un código QR asociado al local y lo coloca en un lugar visible.</li>
                  <li>El cliente final escanea el código QR con su dispositivo móvil.</li>
                  <li>El sistema valida la visita mediante geolocalización o PIN de respaldo.</li>
                  <li>El cliente introduce su número de WhatsApp para registrar el sello.</li>
                  <li>Al completar los sellos requeridos, el cliente recibe un voucher de recompensa vía WhatsApp.</li>
                  <li>El comercio puede verificar y canjear el voucher desde su panel de control.</li>
                </ol>
              </div>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.2. Uso de WhatsApp</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>WhatsApp es un servicio de terceros operado por WhatsApp LLC / Meta Platforms.</li>
                <li>Stampply utiliza la API oficial de WhatsApp Business para el envío de notificaciones.</li>
                <li>Las comunicaciones enviadas son estrictamente operativas: confirmación de sellos y entrega de recompensas.</li>
                <li>El cliente final inicia voluntariamente la interacción al escanear el código QR.</li>
                <li>Stampply no envía comunicaciones promocionales propias a los clientes finales.</li>
              </ul>

              <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.3. Validación de visitas</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>La validación principal se realiza mediante geolocalización del dispositivo del cliente.</li>
                <li>Se establece un radio de geofence configurable por el comercio (por defecto 100 metros).</li>
                <li>En caso de fallo de la geolocalización, se ofrece un PIN de respaldo único por dispositivo.</li>
                <li>Stampply aplica medidas de rate-limiting para prevenir abusos.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                8. Obligaciones del comercio
              </h2>
              <p className="text-slate-700 mb-4">El comercio se compromete a:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>Cumplir con toda la normativa aplicable a su actividad comercial, incluyendo protección de consumidores, publicidad y protección de datos.</li>
                <li>Definir condiciones claras y transparentes para sus programas de fidelización.</li>
                <li>Ser el único responsable de las recompensas ofrecidas, su disponibilidad y cumplimiento.</li>
                <li>Mantener actualizada la información de su cuenta, locales y campañas.</li>
                <li>Informar adecuadamente a sus clientes finales sobre el funcionamiento del programa de fidelización.</li>
                <li>No utilizar el servicio para fines ilícitos, fraudulentos o contrarios a la buena fe.</li>
                <li>Custodiar adecuadamente las credenciales de acceso y notificar cualquier uso no autorizado.</li>
                <li>Respetar los límites de uso establecidos en su plan de suscripción.</li>
              </ul>
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mt-4 rounded-r-lg">
                <p className="text-amber-800 font-medium">
                  ⚠️ Stampply no asume responsabilidad alguna frente al cliente final por el contenido, disponibilidad o cumplimiento de las ofertas y recompensas del comercio.
                </p>
              </div>
            </section>

            {/* Section 9 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                9. Uso permitido y prohibiciones
              </h2>
              <p className="text-slate-700 mb-4">Queda expresamente prohibido:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                <li>Manipular el sistema para generar sellos fraudulentos.</li>
                <li>Usar el servicio para fines ilícitos, spam o prácticas engañosas.</li>
                <li>Interferir con el funcionamiento del servicio (bots maliciosos, ataques, etc.).</li>
                <li>Suplantar identidades o acceder sin autorización a cuentas ajenas.</li>
                <li>Revender o sublicenciar el acceso al servicio sin autorización.</li>
                <li>Utilizar el servicio para recopilar datos de usuarios sin su consentimiento.</li>
              </ul>
            </section>

            {/* Section 10 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                10. Medidas antifraude y moderación
              </h2>
              <p className="text-slate-700 mb-4">
                Stampply podrá aplicar medidas razonables de prevención del fraude (por ejemplo, límites de frecuencia, validaciones de uso, controles por anomalías) para proteger a comercios y clientes finales y preservar la integridad del sistema.
              </p>
              <p className="text-slate-700">
                En caso de uso indebido, Stampply podrá suspender temporal o definitivamente el acceso del comercio, previa evaluación del caso, y sin perjuicio de acciones legales.
              </p>
            </section>

            {/* Section 11 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                11. Propiedad intelectual
              </h2>
              <p className="text-slate-700">
                Stampply y sus licenciatarios conservan todos los derechos sobre la plataforma, software, marca y contenidos. Se concede al comercio una licencia limitada, no exclusiva e intransferible, para usar el servicio mientras dure su relación contractual.
              </p>
            </section>

            {/* Section 12 - Uso de nombre y logo */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                12. Uso de nombre comercial y logotipo
              </h2>
              <p className="text-slate-700 mb-4">
                Al registrarse en Stampply, el comercio autoriza expresamente a Stampply a utilizar su nombre comercial y logotipo con fines promocionales y de marketing, incluyendo pero no limitándose a:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4 mb-4">
                <li>Exhibición en la página web de Stampply como ejemplo de cliente o caso de éxito.</li>
                <li>Inclusión en carruseles de logos de clientes en materiales promocionales.</li>
                <li>Menciones en redes sociales, presentaciones comerciales y comunicaciones de marketing.</li>
                <li>Uso en estudios de caso o testimonios (previa aprobación del contenido específico).</li>
              </ul>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mb-4">
                <p className="text-blue-800">
                  <strong>Alcance de la autorización:</strong> Esta cesión de uso se limita estrictamente a la promoción de los servicios de Stampply y no implica transferencia de derechos de propiedad intelectual sobre la marca o logotipo del comercio.
                </p>
              </div>
              <p className="text-slate-700 mb-4">
                El comercio declara que dispone de los derechos necesarios sobre su nombre comercial y logotipo para otorgar esta autorización, y que dicho uso no vulnera derechos de terceros.
              </p>
              <p className="text-slate-700">
                La visibilidad pública del logotipo del comercio será gestionada por Stampply, quien seleccionará discrecionalmente qué comercios aparecen en sus materiales promocionales.
              </p>
            </section>

            {/* Section 13 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                13. Confidencialidad
              </h2>
              <p className="text-slate-700">
                Las partes se comprometen a no divulgar información confidencial a la que accedan por razón de la prestación del servicio, salvo obligación legal.
              </p>
            </section>

            {/* Section 14 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                14. Responsabilidad y garantías
              </h2>
              <p className="text-slate-700 mb-4">
                El servicio se presta "tal cual". Stampply no garantiza resultados comerciales concretos (incremento de ventas, recurrencia, etc.). En la medida permitida por la ley, Stampply no será responsable de daños indirectos, lucro cesante o pérdidas derivadas de decisiones comerciales del comercio.
              </p>
              <div className="bg-slate-50 border-l-4 border-slate-400 p-4 rounded-r-lg">
                <p className="text-slate-700">
                  En particular, Stampply no responde frente al cliente final por recompensas, ofertas, precios o condiciones del comercio.
                </p>
              </div>
            </section>

            {/* Section 15 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                15. Protección de datos
              </h2>
              <p className="text-slate-700">
                El tratamiento de datos personales se rige por la <Link to="/privacidad" className="text-primary font-medium hover:underline">Política de Privacidad</Link>. En su caso, si Stampply actúa como encargado del tratamiento frente al comercio, se formalizarán las condiciones correspondientes (DPA) o se pondrán a disposición en el servicio.
              </p>
            </section>

            {/* Section 16 */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                15. Modificaciones
              </h2>
              <p className="text-slate-700">
                Stampply puede actualizar estos Términos por motivos legales, técnicos o de mejora del servicio. Publicará la versión vigente en el sitio web e indicará la fecha de última actualización.
              </p>
            </section>

            {/* Section 17 */}
            <section className="mb-6">
              <h2 className="text-2xl font-bold text-primary border-b-2 border-primary/20 pb-2 mb-4">
                17. Legislación aplicable y jurisdicción
              </h2>
              <p className="text-slate-700">
                Se aplicará la legislación española. Salvo norma imperativa en contrario, las partes se someten a los juzgados y tribunales de <strong>Barcelona, España</strong>.
              </p>
            </section>

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsPage;
